import { call, put, takeLatest, select } from 'redux-saga/effects';

import { types, domainType } from '../constants';
import * as eventsApi from '../api/events';
import { submitMode } from '../constants';
import * as attachmentApi from '../api/attachment';
import {
  getRecordAttachmentsToBeAdded,
  getRecordAttachmentsToBeDeleted
} from '../utils/attachmentUtils';

function* getLoggedInUserId() {
  const extractLoggedInUserId = (state) => state.users.user.item;

  const loggedInUser = yield select(extractLoggedInUserId);
  const loggedInUserId = loggedInUser && loggedInUser.id;
  return loggedInUserId;
}

function* watchFetchAllEventsForBranch({ branchId }) {
  try {
    yield put({ type: types.START_FETCH_ALL_EVENTS_FOR_BRANCH });

    const { jsonData } = yield call(eventsApi.getEventsByBranch, branchId);
    yield put({
      type: types.FETCH_ALL_EVENTS_FOR_BRANCH_SUCCESS,
      events: jsonData
    });
  } catch (e) {
    yield resolveError(types.FETCH_ALL_EVENTS_FOR_BRANCH_FAILURE, e);
  } finally {
    yield put({ type: types.FETCH_ALL_EVENTS_FOR_BRANCH_FINISHED });
  }
}

function* watchFetchAllEventsForCompany({ companyId }) {
  try {
    yield put({ type: types.START_FETCH_ALL_EVENTS_FOR_COMPANY });

    const { jsonData } = yield call(eventsApi.getEventsByCompany, companyId);
    yield put({
      type: types.FETCH_ALL_EVENTS_FOR_COMPANY_SUCCESS,
      events: jsonData
    });
  } catch (e) {
    yield resolveError(types.FETCH_ALL_EVENTS_FOR_COMPANY_FAILURE, e);
  } finally {
    yield put({ type: types.FETCH_ALL_EVENTS_FOR_COMPANY_FINISHED });
  }
}

function* watchCreateEditEvent({ event, mode }) {
  try {
    const loggedInUserId = yield getLoggedInUserId();
    yield put({ type: types.START_SUBMIT_EVENT });
    const { jsonData } =
      mode === submitMode.CREATE
        ? yield call(eventsApi.createEvent, event, loggedInUserId)
        : yield call(eventsApi.editEvent, event, loggedInUserId);
    yield put({
      type: types.SUBMIT_EVENT_SUCCESS,
      submittedEvent: jsonData
    });

    if (mode === submitMode.CREATE) {
      yield put({ type: types.OPEN_CREATE_EVENT_DIALOG, open: false });
    } else {
      yield put({ type: types.OPEN_EDIT_EVENT_DIALOG, open: false });
      yield put({
        type: types.FETCH_ALL_EVENTS_FOR_BRANCH,
        branchId: event.branchId
      });
    }
  } catch (e) {
    yield resolveError(types.SUBMIT_EVENT_FAILURE, e);
  } finally {
    yield put({ type: types.SUBMIT_EVENT_FINISHED });
  }
}

function* watchDeleteEvent({ event }) {
  try {
    const loggedInUserId = yield getLoggedInUserId();
    yield put({ type: types.START_DELETE_EVENT });

    yield call(eventsApi.deleteEvent, event.id, loggedInUserId);
    yield put({
      type: types.DELETE_EVENT_SUCCESS,
      eventId: event.id
    });
    yield put({ type: types.OPEN_DELETE_EVENT_DIALOG, open: false });
  } catch (e) {
    yield resolveError(types.DELETE_EVENT_FAILURE, e);
  } finally {
    yield put({ type: types.DELETE_EVENT_FINISHED });
  }
}

function* watchFetchRecordsForTheEvent({ eventId }) {
  try {
    yield put({ type: types.START_FETCH_RECORDS_FOR_THE_EVENT });

    const { jsonData } = yield call(eventsApi.getRecordsForEvent, eventId);
    yield put({
      type: types.FETCH_RECORDS_FOR_THE_EVENT_SUCCESS,
      records: jsonData
    });
  } catch (e) {
    yield resolveError(types.FETCH_RECORDS_FOR_THE_EVENT_FAILURE, e);
  } finally {
    yield put({ type: types.FETCH_RECORDS_FOR_THE_EVENT_FINISHED });
  }
}

function* watchDeleteEventRecord({ payload }) {
  try {
    const loggedInUserId = yield getLoggedInUserId();
    yield put({ type: types.START_DELETE_EVENT_RECORD });

    yield call(eventsApi.deleteEventRecord, payload, loggedInUserId);
    yield put({
      type: types.DELETE_EVENT_RECORD_SUCCESS,
      recordId: payload.recordId
    });
  } catch (e) {
    yield resolveError(types.DELETE_EVENT_RECORD_FAILURE, e);
  } finally {
    yield put({ type: types.DELETE_EVENT_RECORD_FINISHED });
  }
}

function* watchSubmitEventRecord({ payload, mode, branchId }) {
  try {
    const loggedInUserId = yield getLoggedInUserId();
    yield put({ type: types.START_SUBMIT_EVENT_RECORD });

    const { jsonData } =
      mode === submitMode.CREATE
        ? yield call(eventsApi.createRecord, payload, loggedInUserId)
        : yield call(eventsApi.updateRecord, payload, loggedInUserId);

    const domainTypeId = domainType.EVENT_RECORD;

    //Attachments
    //upload attachments if there is any
    const attachments = getRecordAttachmentsToBeAdded(payload);
    if (typeof attachments !== 'undefined' && attachments.length > 0) {
      let parentId = jsonData.id;
      yield put({
        type: types.UPLOAD_ATTACHMENTS_FOR_EVENT_RECORD,
        domainTypeId,
        parentId,
        branchId,
        attachments
      });
    }

    //Delete attachments if any
    const attachmentToDelete = getRecordAttachmentsToBeDeleted(payload);
    if (
      typeof attachmentToDelete !== 'undefined' &&
      attachmentToDelete.length > 0
    ) {
      let attachmentIdsArray = attachmentToDelete.map((g) => g.id);
      yield put({
        type: types.DELETE_ATTACHMENTS_FOR_EVENT_RECORD,
        attachmentIdsArray,
        loggedInUserId,
        branchId
      });
    }

    yield put({
      type: types.SUBMIT_EVENT_RECORD_SUCCESS,
      createdRecord: jsonData
    });

    yield put({
      type: types.FETCH_RECORDS_FOR_THE_EVENT,
      eventId: payload.eventId
    });

    if (mode === submitMode.UPDATE) {
      yield put({
        type: types.ENABLE_EDIT_EVENT_RECORD,
        enableEditRecordMode: false,
        recordId: payload.id
      });
    } else {
      yield put({
        type: types.OPEN_ADD_EVENT_RECORD_DIALOG,
        open: false
      });
    }
  } catch (e) {
    yield resolveError(types.SUBMIT_EVENT_RECORD_FAILURE, e);
  } finally {
    yield put({ type: types.SUBMIT_EVENT_RECORD_FINISHED });
  }
}

//attachments

function* watchLoadAttachmentFilesForEventRecord({
  domainTypeId,
  parentId,
  loggedInUserId
}) {
  try {
    yield put({ type: types.START_LOAD_ATTACHMENTS_FOR_EVENT_RECORD });
    const { jsonData } = yield call(
      attachmentApi.getAttachmentsForSingle,
      domainTypeId,
      parentId,
      loggedInUserId
    );
    yield put({
      type: types.LOAD_ATTACHMENTS_FOR_EVENT_RECORD_SUCCESS,
      attachments: jsonData
    });
  } catch (error) {
    yield resolveError(types.LOAD_ATTACHMENTS_FOR_EVENT_RECORD_FAILURE, error);
  } finally {
    yield put({ type: types.LOAD_ATTACHMENTS_FOR_EVENT_RECORD_FINISHED });
  }
}

function* watchDeleteAttachmentsForEventRecord({
  attachmentIdsArray,
  loggedInUserId,
  branchId
}) {
  try {
    if (
      typeof attachmentIdsArray !== 'undefined' &&
      attachmentIdsArray.length > 0
    ) {
      const attachmentIdsString = attachmentIdsArray.join(';');
      yield call(
        attachmentApi.deleteAttachments,
        attachmentIdsString,
        loggedInUserId,
        branchId
      );
    }
  } catch (error) {
  } finally {
  }
}

function* watchUploadAttachmentsForEvent({
  domainTypeId,
  parentId,
  branchId,
  attachments
}) {
  try {
    yield call(
      attachmentApi.addAttachments,
      domainTypeId,
      parentId,
      branchId,
      attachments
    );
  } catch (error) {
  } finally {
  }
}

//This is used to catch all the errors in case of request unsuccessful, and will extract the actual error message.
function* resolveError(type, error) {
  let errorMessageCaptured = false;
  let completeErrorMessage = {};
  if (!error.ok) {
    try {
      const clone = error.clone();
      yield clone.text().then((text) => {
        completeErrorMessage = JSON.parse(text);
        errorMessageCaptured = true;
      });
    } catch (err) {
      errorMessageCaptured = true;
      completeErrorMessage = err;
    }

    if (errorMessageCaptured) {
      const errorMessage = extractErrorMessage(completeErrorMessage);
      yield put({ type, errorMessage });
    }
  }
}

function extractErrorMessage(completeErrorMessage) {
  let errorMessage = '';

  try {
    const message = completeErrorMessage.exceptionMessage
      ? completeErrorMessage.exceptionMessage.split('Content:')[1]
      : completeErrorMessage.messageDetail;

    errorMessage = JSON.parse(message).errorMessage;
    if (!errorMessage) {
      errorMessage = 'An error occured';
    }
  } catch (error) {
    errorMessage = 'An error occured';
  }

  return errorMessage;
}
export default function* BranchesWatcher() {
  yield takeLatest(
    types.FETCH_ALL_EVENTS_FOR_BRANCH,
    watchFetchAllEventsForBranch
  );
  yield takeLatest(
    types.FETCH_ALL_EVENTS_FOR_COMPANY,
    watchFetchAllEventsForCompany
  );
  yield takeLatest(types.SUBMIT_EVENT, watchCreateEditEvent);
  yield takeLatest(types.DELETE_EVENT, watchDeleteEvent);

  yield takeLatest(
    types.FETCH_RECORDS_FOR_THE_EVENT,
    watchFetchRecordsForTheEvent
  );

  yield takeLatest(types.DELETE_EVENT_RECORD, watchDeleteEventRecord);
  yield takeLatest(types.SUBMIT_EVENT_RECORD, watchSubmitEventRecord);

  yield takeLatest(
    types.UPLOAD_ATTACHMENTS_FOR_EVENT_RECORD,
    watchUploadAttachmentsForEvent
  );

  yield takeLatest(
    types.DELETE_ATTACHMENTS_FOR_EVENT_RECORD,
    watchDeleteAttachmentsForEventRecord
  );

  yield takeLatest(
    types.LOAD_ATTACHMENTS_FOR_EVENT_RECORD,
    watchLoadAttachmentFilesForEventRecord
  );
}
